<script setup>
// Um aviso da Central: o que aconteceu (título), o que fazer (próximo passo),
// quantas vezes e quando por último — e o atalho pro objeto (conversa, regra, webhook, bot, agente, time).
// Fechar é decisão de quem é dono do aviso: resolvido ou ignorado.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { timeAgo } from 'dashboard/helper/timeAgo';
import { toast } from 'dashboard/helper/toast';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  notice: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const isSaving = ref(false);

const SEVERITY_STYLES = {
  critical: {
    icon: 'i-lucide-octagon-alert',
    tone: 'text-n-ruby-11 bg-n-ruby-3',
  },
  warning: {
    icon: 'i-lucide-triangle-alert',
    tone: 'text-n-amber-11 bg-n-amber-3',
  },
  info: { icon: 'i-lucide-info', tone: 'text-n-blue-11 bg-n-blue-3' },
};

const EXTERNAL_TOOL_KINDS = [
  'ferramenta_externa_com_falha',
  'ferramenta_externa_executor_indisponivel',
];

const severityStyle = computed(
  () => SEVERITY_STYLES[props.notice.severity] || SEVERITY_STYLES.info
);

const subjectLink = computed(() => {
  const { subject } = props.notice;
  if (subject?.type === 'conversation') {
    const messageId = props.notice.context?.message_id;
    return {
      to: accountScopedRoute(
        'inbox_conversation',
        { conversation_id: subject.display_id },
        messageId ? { messageId } : {}
      ),
      label: subject.contact_name
        ? t('OPS_NOTICES.OPEN_CONVERSATION_OF', { name: subject.contact_name })
        : t('OPS_NOTICES.OPEN_CONVERSATION'),
    };
  }
  if (subject?.type === 'webhook') {
    return {
      to: accountScopedRoute('settings_integrations_webhook'),
      label: t('OPS_NOTICES.OPEN_WEBHOOKS'),
    };
  }
  if (subject?.type === 'agent_bot') {
    return {
      to: accountScopedRoute('agent_bots'),
      label: t('OPS_NOTICES.OPEN_AGENT_BOTS'),
    };
  }
  if (subject?.type === 'agent') {
    return {
      to: accountScopedRoute('agent_list'),
      label: t('OPS_NOTICES.OPEN_AGENT', { name: subject.name }),
    };
  }
  if (subject?.type === 'agent_list') {
    return {
      to: accountScopedRoute('agent_list'),
      label: t('OPS_NOTICES.OPEN_AGENTS'),
    };
  }
  if (subject?.type === 'team') {
    return {
      to: accountScopedRoute('settings_teams_edit', { teamId: subject.id }),
      label: t('OPS_NOTICES.OPEN_TEAM', { name: subject.name }),
    };
  }
  if (subject?.type === 'automation_rule') {
    return {
      to: accountScopedRoute('automation_list'),
      label: t('OPS_NOTICES.OPEN_AUTOMATION'),
    };
  }
  // Aviso nascido de sinal não tem objeto: o atalho sai do tipo. Ferramenta
  // externa se conserta no Studio, não em Integrações › Webhooks — na conta 325
  // (2026-10-21) o único aviso de "webhook" à vista era de outro destino.
  if (EXTERNAL_TOOL_KINDS.includes(props.notice.kind)) {
    return {
      to: accountScopedRoute('studio_external_tools'),
      label: t('OPS_NOTICES.OPEN_EXTERNAL_TOOLS'),
    };
  }
  return null;
});

// Conversas do aviso (fila parada, agente inativo, bot sem cadastro), com o
// nome do contato: quem recebe o aviso quer abrir a conversa, não o número.
const conversations = computed(() => {
  const list = props.notice.conversations;
  if (!list?.items?.length) return null;
  return {
    hidden: list.total - list.items.length,
    items: list.items.map(item => ({
      key: item.display_id,
      to: accountScopedRoute('inbox_conversation', {
        conversation_id: item.display_id,
      }),
      name: item.contact_name || t('OPS_NOTICES.CONTACT_WITHOUT_NAME'),
      waiting: item.waiting_since
        ? t('OPS_NOTICES.WAITING_SINCE', { time: timeAgo(item.waiting_since) })
        : '',
    })),
  };
});

// Pessoas do aviso por conta (cadastros parados), com o último acesso: é o que
// diz ao administrador se a pessoa ainda trabalha ali.
const people = computed(() => {
  const list = props.notice.people;
  if (!list?.items?.length) return null;
  return {
    hidden: list.total - list.items.length,
    items: list.items.map(item => ({
      key: item.id,
      name: item.name,
      lastSignIn: item.last_sign_in_at
        ? t('OPS_NOTICES.LAST_SIGN_IN', { time: timeAgo(item.last_sign_in_at) })
        : t('OPS_NOTICES.NEVER_SIGNED_IN'),
    })),
  };
});

const lastSeen = computed(() =>
  t('OPS_NOTICES.LAST_SEEN', { time: timeAgo(props.notice.last_seen_at) })
);

const close = async resolution => {
  if (isSaving.value) return;
  isSaving.value = true;
  // toast.promise do vue-sonner 2.0.9 devolve o id do toast, não a promise:
  // com `await` nele o botão destravava com o POST em voo e o 2º clique
  // mandava outro resolve (toast de erro falso). Espera-se a MESMA promise.
  const request = store.dispatch('opsNotices/resolve', {
    id: props.notice.id,
    resolution,
  });
  toast.promise(request, {
    loading: t('OPS_NOTICES.RESOLVING'),
    success: t('OPS_NOTICES.RESOLVED_SUCCESS'),
    error: t('OPS_NOTICES.RESOLVE_ERROR'),
  });
  try {
    await request;
  } catch {
    // Já divulgado pelo toast de erro acima.
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <li
    class="flex gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
    :data-test-id="`ops-notice-${notice.id}`"
  >
    <span
      class="flex items-center justify-center flex-shrink-0 rounded-lg size-8"
      :class="severityStyle.tone"
    >
      <span class="size-4" :class="severityStyle.icon" />
    </span>
    <div class="flex flex-col flex-1 min-w-0 gap-3 lg:flex-row lg:gap-6">
      <div class="flex flex-col flex-1 min-w-0 gap-2">
        <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ notice.title }}
          </h3>
          <span class="text-xs text-n-slate-11 tabular-nums">
            {{ t('OPS_NOTICES.OCCURRENCES', { count: notice.occurrences }) }}
            · {{ lastSeen }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5 max-w-3xl">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('OPS_NOTICES.NEXT_STEP') }}
          </span>
          <p
            class="m-0 text-sm text-n-slate-12 text-pretty"
            data-test-id="ops-notice-next-step"
          >
            {{ notice.next_step }}
          </p>
          <p
            v-if="notice.subject?.url"
            v-tooltip="notice.subject.url"
            class="m-0 mt-1 font-mono text-xs truncate text-n-slate-11"
            data-test-id="ops-notice-subject-url"
          >
            {{ notice.subject.url }}
          </p>
        </div>
        <div
          v-if="conversations"
          class="flex flex-col gap-1"
          data-test-id="ops-notice-conversations"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('OPS_NOTICES.CONVERSATIONS') }}
          </span>
          <ul class="flex flex-col gap-1 m-0 list-none reset-base">
            <li
              v-for="item in conversations.items"
              :key="item.key"
              class="flex flex-wrap items-baseline gap-x-2"
            >
              <RouterLink
                :to="item.to"
                class="text-sm font-medium text-n-blue-11 hover:underline"
                data-test-id="ops-notice-conversation-link"
              >
                {{ item.name }}
              </RouterLink>
              <span
                v-if="item.waiting"
                class="text-xs text-n-slate-11 tabular-nums"
              >
                {{ item.waiting }}
              </span>
            </li>
          </ul>
          <span v-if="conversations.hidden > 0" class="text-xs text-n-slate-11">
            {{
              t('OPS_NOTICES.MORE_CONVERSATIONS', {
                count: conversations.hidden,
              })
            }}
          </span>
        </div>
        <div
          v-if="people"
          class="flex flex-col gap-1"
          data-test-id="ops-notice-people"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('OPS_NOTICES.PEOPLE') }}
          </span>
          <ul class="flex flex-col gap-1 m-0 list-none reset-base">
            <li
              v-for="item in people.items"
              :key="item.key"
              class="flex flex-wrap items-baseline gap-x-2"
              data-test-id="ops-notice-person"
            >
              <span class="text-sm text-n-slate-12">{{ item.name }}</span>
              <span class="text-xs text-n-slate-11">{{ item.lastSignIn }}</span>
            </li>
          </ul>
          <span v-if="people.hidden > 0" class="text-xs text-n-slate-11">
            {{ t('OPS_NOTICES.MORE_PEOPLE', { count: people.hidden }) }}
          </span>
        </div>
        <RouterLink
          v-if="subjectLink"
          :to="subjectLink.to"
          class="self-start text-sm font-medium text-n-blue-11 hover:underline"
          data-test-id="ops-notice-subject-link"
        >
          {{ subjectLink.label }}
        </RouterLink>
      </div>
      <div
        class="flex flex-wrap items-start flex-shrink-0 gap-2 lg:justify-end"
      >
        <Button
          v-tooltip="t('OPS_NOTICES.IGNORE_HINT')"
          class="flex-shrink-0"
          :label="t('OPS_NOTICES.IGNORE')"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="isSaving"
          data-test-id="ops-notice-ignore"
          @click="close('ignored')"
        />
        <Button
          v-tooltip="t('OPS_NOTICES.RESOLVE_HINT')"
          class="flex-shrink-0"
          :label="t('OPS_NOTICES.RESOLVE')"
          color="teal"
          size="sm"
          icon="i-lucide-check"
          :is-loading="isSaving"
          data-test-id="ops-notice-resolve"
          @click="close('fixed')"
        />
      </div>
    </div>
  </li>
</template>
